import { useState, useEffect, FormEvent } from 'react';
import {
  getPlayers,
  getTeams,
  createPlayer,
  updatePlayer,
  archivePlayer,
  Player,
  PlayerStatus,
  PlayerStatsForSeason,
  Team,
} from '../api/client';
import ImageUploader from '../components/ImageUploader';
import TeamNav from '../components/TeamNav';
import GuardianEditor from '../components/GuardianEditor';
import { useAuth } from '../contexts/AuthContext';
import { useSportConfig } from '../hooks/useSportConfig';
import './Players.css';

// Stat keys and labels come from the club's sport (sportConfig.stats.keys).

const CONSENT_METHODS = [
  { value: '', label: ', Not yet recorded, ' },
  { value: 'verbal', label: 'Verbal (in person / phone)' },
  { value: 'email', label: 'Email confirmation' },
  { value: 'signed_form', label: 'Signed paper form' },
  { value: 'online_form', label: 'Online form' },
];

// Positions come from the club's sport (useSportConfig); nothing is hardcoded here.

// Any public-visibility flag being on means we are publishing this
// player's data. That requires a recorded parental consent record.
function isPublicallyVisible(pf: Player['publicFields']): boolean {
  if (!pf) return false;
  return Boolean(
    pf.showOnSquadPage ||
    pf.showPhoto ||
    pf.showLastName ||
    pf.showJerseyNumber ||
    pf.showPosition ||
    pf.showBio ||
    pf.showAchievements ||
    pf.showStats,
  );
}

// Consent is "recorded" when both the method and the parent/guardian
// name are filled in. Notes and email are optional supporting evidence.
function hasRecordedConsent(consent: Player['consent']): boolean {
  if (!consent) return false;
  return Boolean(consent.method && consent.parentName && consent.parentName.trim());
}

/**
 * One season's stats as edited in the modal. Kept as an array (not a
 * Record) so:
 *   - the user can rename a season without losing key-order
 *   - duplicate-season detection happens at save time, not while typing
 *   - the order of rows in the modal matches the order we render
 */
interface SeasonEntry {
  season: string;
  stats: PlayerStatsForSeason;
}

function emptyStatsForSeason(keys: readonly string[]): PlayerStatsForSeason {
  const out: PlayerStatsForSeason = {};
  for (const k of keys) out[k] = 0;
  return out;
}

/** The season label for today, in the sport's form: "2026-27" or "2026". */
function currentSeason(season: { startMonth: number; label: 'split' | 'single' }): string {
  const d = new Date();
  const month = d.getMonth() + 1; // 1 = Jan
  const startYear = month >= season.startMonth ? d.getFullYear() : d.getFullYear() - 1;
  return season.label === 'split' ? `${startYear}-${String(startYear + 1).slice(2)}` : String(startYear);
}

interface FormState {
  teamId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  position: string;
  jerseyNumber: string; // string for input control
  photoUrl: string;
  bio: string;
  achievementsText: string; // newline-separated in the form
  joinedDate: string;
  status: PlayerStatus;
  publicFields: Player['publicFields'];
  consent: Player['consent'];
}

function emptyForm(): FormState {
  return {
    teamId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    position: '',
    jerseyNumber: '',
    photoUrl: '',
    bio: '',
    achievementsText: '',
    joinedDate: new Date().toISOString().slice(0, 10),
    status: 'active',
    publicFields: {
      showOnSquadPage: false,
      showPhoto: false,
      showLastName: false,
      showJerseyNumber: false,
      showPosition: false,
      showBio: false,
      showAchievements: false,
      showStats: false,
    },
    consent: {},
  };
}

function playerToForm(p: Player): FormState {
  return {
    teamId: p.teamId || '',
    firstName: p.firstName || '',
    lastName: p.lastName || '',
    dateOfBirth: p.dateOfBirth || '',
    position: p.position || '',
    jerseyNumber: p.jerseyNumber == null ? '' : String(p.jerseyNumber),
    photoUrl: p.photoUrl || '',
    bio: p.bio || '',
    achievementsText: (p.achievements || []).join('\n'),
    joinedDate: p.joinedDate || '',
    status: p.status || 'active',
    publicFields: { ...emptyForm().publicFields, ...(p.publicFields || {}) },
    consent: { ...(p.consent || {}) },
  };
}

function formToApi(f: FormState): Partial<Player> {
  return {
    teamId: f.teamId,
    firstName: f.firstName,
    lastName: f.lastName,
    dateOfBirth: f.dateOfBirth,
    position: f.position,
    jerseyNumber: f.jerseyNumber === '' ? null : Number(f.jerseyNumber),
    photoUrl: f.photoUrl,
    bio: f.bio,
    achievements: f.achievementsText.split('\n').map(s => s.trim()).filter(Boolean),
    joinedDate: f.joinedDate,
    status: f.status,
    publicFields: f.publicFields,
    consent: f.consent,
  };
}

function teamLabel(team: Team | undefined): string {
  if (!team) return '(team removed)';
  return team.ageGroup ? `${team.name} (${team.ageGroup})` : team.name;
}

export default function Players() {
  const sportConfig = useSportConfig();
  const statKeys = sportConfig.stats.keys.map(k => k.key);
  const statLabel = (k: string) => sportConfig.stats.keys.find(d => d.key === k)?.label || k;
  const derivedKeys = new Set(Object.keys(sportConfig.stats.derived));
  const { features } = useAuth();
  const membersEnabled = features.members === true;
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Stats modal, stacked seasons. Each entry is one season + its stat
  // fields. New player starts with one entry (current season, zeros);
  // "+ Add season" appends another entry below.
  const [statsModalPlayer, setStatsModalPlayer] = useState<Player | null>(null);
  const [statsSeasons, setStatsSeasons] = useState<SeasonEntry[]>([]);
  const [isSavingStats, setIsSavingStats] = useState(false);

  // Filter UI.
  // teamFilter values:
  //   '__all', DEFAULT, full roster grouped by team (the overview)
  //   teamId, narrow to that team's squad
  const [teamFilter, setTeamFilter] = useState<string>('__all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState<string>('');
  // Table is the default, better for clubs with dozens of players.
  // Preference is remembered across sessions.
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(
    () => (localStorage.getItem('players.viewMode') === 'cards' ? 'cards' : 'table'),
  );
  const setView = (mode: 'table' | 'cards') => {
    setViewMode(mode);
    localStorage.setItem('players.viewMode', mode);
  };

  useEffect(() => { void loadAll(); }, []);

  // Single-team clubs: narrow straight to that team, the grouped
  // overview would just be one section, so skip the extra header.
  useEffect(() => {
    if (teamFilter === '__all' && teams.length === 1 && teams[0]) {
      setTeamFilter(teams[0].id);
    }
  }, [teams, teamFilter]);

  async function loadAll() {
    try {
      setIsLoading(true);
      const [pl, tm] = await Promise.all([getPlayers(), getTeams()]);
      setPlayers(pl);
      setTeams(tm);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load players');
    } finally {
      setIsLoading(false);
    }
  }

  function openCreate() {
    setEditingPlayer(null);
    const fresh = emptyForm();
    // Default the new player's team to the currently-selected team if a
    // specific team is in view; otherwise to the first team in the list.
    const defaultTeamId =
      teamFilter && teamFilter !== '__all'
        ? teamFilter
        : teams[0]?.id;
    if (defaultTeamId) fresh.teamId = defaultTeamId;
    setForm(fresh);
    setShowFormModal(true);
  }

  function openEdit(p: Player) {
    setEditingPlayer(p);
    setForm(playerToForm(p));
    setShowFormModal(true);
  }

  function closeForm() {
    setShowFormModal(false);
    setEditingPlayer(null);
    setForm(emptyForm());
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (isUploadingPhoto) {
      setError('Wait for photo upload to finish before saving');
      return;
    }
    if (!form.firstName.trim()) { setError('First name is required'); return; }
    if (!form.teamId) { setError('Team is required'); return; }

    // Public-by-consent gate: if anything is being published, parental
    // consent must be on file (method + parent name at minimum).
    if (isPublicallyVisible(form.publicFields) && !hasRecordedConsent(form.consent)) {
      setError(
        'Parental consent is required before publishing this player. ' +
        'Record the consent method and parent / guardian name, or untick all public visibility fields.',
      );
      return;
    }

    // Jersey conflict warning, non-blocking. Numbers swapping mid-season is
    // common in grassroots; we warn rather than reject so admins can confirm
    // and continue.
    if (form.jerseyNumber !== '' && form.teamId) {
      const newJersey = Number(form.jerseyNumber);
      const conflict = players.find(p =>
        p.teamId === form.teamId &&
        p.status !== 'archived' &&
        p.playerId !== editingPlayer?.playerId &&
        p.jerseyNumber === newJersey,
      );
      if (conflict) {
        const ok = confirm(
          `${conflict.firstName} ${conflict.lastName} already wears #${newJersey} for this team. ` +
          `Save anyway? (You can update their number afterwards.)`,
        );
        if (!ok) return;
      }
    }

    try {
      setIsSaving(true);
      const payload = formToApi(form);
      if (editingPlayer) {
        await updatePlayer(editingPlayer.playerId, payload);
        setSuccessMessage(`Updated ${form.firstName}`);
      } else {
        await createPlayer(payload);
        setSuccessMessage(`Added ${form.firstName}`);
      }
      await loadAll();
      closeForm();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save player');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleArchive(p: Player) {
    if (!confirm(`Archive ${p.firstName}? They'll be hidden from the public squad. Their consent record is kept for our records.`)) return;
    try {
      await archivePlayer(p.playerId);
      setSuccessMessage(`${p.firstName} archived`);
      await loadAll();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive player');
    }
  }

  function openStatsModal(p: Player) {
    setStatsModalPlayer(p);
    // Build the seasons array from the player's existing record. If they
    // have nothing on record, seed with one row for the current season so
    // the user has something to fill in immediately.
    const existing = p.stats || {};
    const keys = Object.keys(existing).sort().reverse();   // most recent first
    if (keys.length === 0) {
      setStatsSeasons([{ season: currentSeason(sportConfig.season), stats: emptyStatsForSeason(statKeys) }]);
    } else {
      setStatsSeasons(keys.map(k => ({
        season: k,
        stats: { ...emptyStatsForSeason(statKeys), ...existing[k] },
      })));
    }
  }

  function closeStatsModal() {
    setStatsModalPlayer(null);
    setStatsSeasons([]);
  }

  /**
   * Append a new season row. Default to the most recent season the player
   * doesn't yet have on record (walks backwards from the current UK
   * football season). If they have all the recent ones, falls back to
   * "(new season)" so the user notices and types their own.
   */
  function addSeasonRow() {
    const taken = new Set(statsSeasons.map(s => s.season));
    const cur = currentSeason(sportConfig.season);
    const startYear = parseInt(cur.slice(0, 4), 10);
    let candidate = '(new season)';
    for (let y = startYear; y >= startYear - 20; y--) {
      const tryKey = `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
      if (!taken.has(tryKey)) { candidate = tryKey; break; }
    }
    setStatsSeasons([...statsSeasons, { season: candidate, stats: emptyStatsForSeason(statKeys) }]);
  }

  function removeSeasonRow(idx: number) {
    if (!confirm(`Remove this season's stats? This cannot be undone (until you Cancel).`)) return;
    setStatsSeasons(statsSeasons.filter((_, i) => i !== idx));
  }

  function updateSeasonName(idx: number, season: string) {
    setStatsSeasons(statsSeasons.map((s, i) => i === idx ? { ...s, season } : s));
  }

  function updateSeasonStat(idx: number, key: keyof PlayerStatsForSeason, value: number) {
    setStatsSeasons(statsSeasons.map((s, i) =>
      i === idx ? { ...s, stats: { ...s.stats, [key]: Math.max(0, value || 0) } } : s
    ));
  }

  async function handleSaveStats() {
    if (!statsModalPlayer) return;
    setError('');

    // Validate season names + reject duplicates BEFORE writing.
    const seasonRe = /^\d{4}(-\d{2})?$/;
    const seasonExample = currentSeason(sportConfig.season);
    const seen = new Set<string>();
    for (const s of statsSeasons) {
      if (!seasonRe.test(s.season)) {
        setError(`Season "${s.season}" must look like ${seasonExample}.`);
        return;
      }
      if (seen.has(s.season)) {
        setError(`Season "${s.season}" appears twice. Combine them or rename one.`);
        return;
      }
      seen.add(s.season);
    }

    try {
      setIsSavingStats(true);
      const newStats: Record<string, PlayerStatsForSeason> = {};
      for (const s of statsSeasons) newStats[s.season] = s.stats;
      await updatePlayer(statsModalPlayer.playerId, { stats: newStats });
      setSuccessMessage(`Stats saved for ${statsModalPlayer.firstName}`);
      await loadAll();
      closeStatsModal();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save stats');
    } finally {
      setIsSavingStats(false);
    }
  }

  const teamMap: Record<string, Team> = Object.fromEntries(teams.map(t => [t.id, t]));
  const searchTerm = search.trim().toLowerCase();
  const groupedView = teamFilter === '__all';
  const visiblePlayers = players.filter(p => {
    // teamFilter '' (picker) or '__all' (cross-cut) both let everything through;
    // any other value is a teamId we filter to.
    if (teamFilter && teamFilter !== '__all' && p.teamId !== teamFilter) return false;
    if (!includeArchived && p.status === 'archived') return false;
    if (searchTerm) {
      const haystack = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      if (!haystack.includes(searchTerm)) return false;
    }
    return true;
  });

  // Group the visible players by team for the grouped-roster overview.
  // Teams render in their configured order; an "Unassigned" bucket
  // catches any player whose teamId no longer matches a team.
  const playersByTeam: Array<{ team: Team | null; teamId: string; players: Player[] }> = [];
  if (groupedView) {
    const byId: Record<string, Player[]> = {};
    for (const p of visiblePlayers) (byId[p.teamId] ||= []).push(p);
    for (const t of teams) {
      const group = byId[t.id];
      if (group && group.length) playersByTeam.push({ team: t, teamId: t.id, players: group });
    }
    for (const id of Object.keys(byId)) {
      if (teamMap[id]) continue; // already rendered above
      const group = byId[id];
      if (group && group.length) playersByTeam.push({ team: null, teamId: id, players: group });
    }
  }

  // One player card, reused by the grouped + single-team views.
  const renderPlayerCard = (p: Player) => (
    <div key={p.playerId} className={`player-card ${p.status === 'archived' ? 'archived' : ''}`}>
      <div className="player-photo">
        {p.photoUrl ? (
          <img src={p.photoUrl} alt="" />
        ) : (
          <div className="player-photo-placeholder">
            {(p.firstName?.[0] || '?').toUpperCase()}{(p.lastName?.[0] || '').toUpperCase()}
          </div>
        )}
        {p.jerseyNumber != null && (
          <span className="player-number">{p.jerseyNumber}</span>
        )}
      </div>
      <div className="player-card-body">
        <h3>{p.firstName} {p.lastName}</h3>
        <p className="player-meta">
          {p.position || '-'} · {teamLabel(teamMap[p.teamId])}
        </p>
        <p className="player-meta-secondary">
          <span className={`badge badge-${p.status}`}>{p.status}</span>
          {p.publicFields?.showOnSquadPage ? (
            <span className="badge badge-public">Public</span>
          ) : (
            <span className="badge badge-private">Private</span>
          )}
        </p>
        {(p.consent?.parentName || p.consent?.parentEmail) && (
          <p className="player-guardian">
            <span className="player-guardian-label">Guardian:</span>{' '}
            {p.consent.parentName || '-'}
            {p.consent.parentEmail && (
              <a className="player-guardian-email" href={`mailto:${p.consent.parentEmail}`}>
                {p.consent.parentEmail}
              </a>
            )}
          </p>
        )}
        <div className="player-card-actions">
          <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => openEdit(p)}>Edit</button>
          <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => openStatsModal(p)}>Stats</button>
          {p.status !== 'archived' && (
            <button className="ebtn ebtn-danger ebtn-sm" onClick={() => handleArchive(p)}>Archive</button>
          )}
        </div>
      </div>
    </div>
  );

  // Table view, the default. Scannable for big squads. Reused by the
  // grouped + single-team views (one <table> per group).
  const renderPlayerTable = (list: Player[]) => (
    <div className="players-table-wrap card-surface">
      <table className="players-table">
        <thead>
          <tr>
            <th className="pt-col-player">Player</th>
            <th className="pt-col-pos">Position</th>
            <th className="pt-col-num">#</th>
            <th className="pt-col-guardian">Guardian</th>
            <th className="pt-col-status">Status</th>
            <th className="pt-col-vis">Visibility</th>
            <th className="pt-col-actions" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {list.map(p => (
            <tr key={p.playerId} className={p.status === 'archived' ? 'pt-archived' : ''}>
              <td className="pt-col-player">
                <div className="pt-player">
                  {p.photoUrl ? (
                    <img className="pt-avatar" src={p.photoUrl} alt="" />
                  ) : (
                    <span className="pt-avatar pt-avatar-empty">
                      {(p.firstName?.[0] || '?').toUpperCase()}{(p.lastName?.[0] || '').toUpperCase()}
                    </span>
                  )}
                  <span className="pt-name">{p.firstName} {p.lastName}</span>
                </div>
              </td>
              <td className="pt-col-pos">{p.position || '-'}</td>
              <td className="pt-col-num">{p.jerseyNumber ?? '-'}</td>
              <td className="pt-col-guardian">
                {p.consent?.parentName || p.consent?.parentEmail ? (
                  <div className="pt-guardian">
                    {p.consent.parentName && <span className="pt-guardian-name">{p.consent.parentName}</span>}
                    {p.consent.parentEmail && (
                      <a className="pt-guardian-email" href={`mailto:${p.consent.parentEmail}`}>
                        {p.consent.parentEmail}
                      </a>
                    )}
                  </div>
                ) : (
                  <span className="pt-guardian-none">-</span>
                )}
              </td>
              <td className="pt-col-status">
                <span className={`badge badge-${p.status}`}>{p.status}</span>
              </td>
              <td className="pt-col-vis">
                {p.publicFields?.showOnSquadPage ? (
                  <span className="badge badge-public">Public</span>
                ) : (
                  <span className="badge badge-private">Private</span>
                )}
              </td>
              <td className="pt-col-actions">
                <div className="pt-actions">
                  <button className="ebtn ebtn-ghost ebtn-sm" onClick={() => openEdit(p)}>Edit</button>
                  <button className="ebtn ebtn-ghost ebtn-sm" onClick={() => openStatsModal(p)}>Stats</button>
                  {p.status !== 'archived' && (
                    <button className="ebtn ebtn-ghost ebtn-sm" onClick={() => handleArchive(p)}>Archive</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPlayers = (list: Player[]) =>
    viewMode === 'table'
      ? renderPlayerTable(list)
      : <div className="players-grid">{list.map(renderPlayerCard)}</div>;

  return (
    <div className="tm-page-shell players-page">
      <TeamNav />

      <div className="page-head">
        <div>
          <h1>Players</h1>
          <p className="subtitle">
            Squad profiles, private by default, photos and personal details stay
            hidden until you tick them on for the public website.
          </p>
        </div>
        <div className="head-actions">
          <button
            className="ebtn ebtn-primary"
            onClick={openCreate}
            disabled={teams.length === 0}
          >
            + Add player
          </button>
        </div>
      </div>

      {error && <div className="ealert ealert-error">{error}</div>}
      {successMessage && <div className="ealert ealert-success">{successMessage}</div>}
      {teams.length === 0 && (
        <div className="ealert ealert-info">
          You need at least one team before you can add players. Head over to the Teams tab first.
        </div>
      )}

      <div className="players-toolbar">
        <input
          type="search"
          className="players-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name…"
          aria-label="Search players by name"
        />
        <select
          value={teamFilter}
          onChange={e => setTeamFilter(e.target.value)}
          aria-label="Filter by team"
        >
          <option value="__all">All teams (grouped)</option>
          {teams.map(t => <option key={t.id} value={t.id}>{teamLabel(t)}</option>)}
        </select>
        <label className="toolbar-toggle">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={e => setIncludeArchived(e.target.checked)}
          />
          Include archived
        </label>
        <div className="players-view-toggle" role="group" aria-label="View mode">
          <button
            type="button"
            className={`pvt-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setView('table')}
            aria-pressed={viewMode === 'table'}
          >
            ☰ Table
          </button>
          <button
            type="button"
            className={`pvt-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setView('cards')}
            aria-pressed={viewMode === 'cards'}
          >
            ▦ Cards
          </button>
        </div>
        <span className="toolbar-count">
          {visiblePlayers.length} player{visiblePlayers.length === 1 ? '' : 's'}
        </span>
      </div>

      {isLoading ? (
        <p className="loading">Loading players…</p>
      ) : visiblePlayers.length === 0 ? (
        <div className="empty-state">
          <p>
            No players yet
            {teamFilter !== '__all' ? ' in this team' : ''}
            {searchTerm ? ' matching your search' : ''}.
          </p>
          {teams.length > 0 && !searchTerm && (
            <button className="ebtn ebtn-primary" onClick={openCreate} style={{ marginTop: 12 }}>
              + Add your first player
            </button>
          )}
        </div>
      ) : groupedView ? (
        // Full roster, grouped by team, the default overview.
        <div className="roster-groups">
          {playersByTeam.map(group => (
            <section key={group.teamId} className="roster-group">
              <div className="roster-group-head">
                <h2 className="roster-group-title">
                  {group.team ? group.team.name : 'Unassigned'}
                  {group.team?.ageGroup && (
                    <span className="roster-group-age">{group.team.ageGroup}</span>
                  )}
                </h2>
                <span className="roster-group-count">
                  {group.players.length} player{group.players.length === 1 ? '' : 's'}
                </span>
              </div>
              {renderPlayers(group.players)}
            </section>
          ))}
        </div>
      ) : (
        // Single team narrowed via the toolbar select.
        renderPlayers(visiblePlayers)
      )}

      {/* ── Add / Edit Modal ─────────────────── */}
      {showFormModal && (
        <div className="players-modal-overlay" onClick={closeForm}>
          <div className="players-modal players-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="players-modal-header">
              <h2>{editingPlayer ? 'Edit player' : 'Add player'}</h2>
              <button type="button" className="players-modal-close" onClick={closeForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="player-form">
              {/* Identity */}
              <fieldset>
                <legend>Identity</legend>
                <div className="form-row">
                  <label>
                    First name *
                    <input
                      type="text"
                      value={form.firstName}
                      onChange={e => setForm({ ...form, firstName: e.target.value })}
                      required
                    />
                  </label>
                  <label>
                    Last name
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={e => setForm({ ...form, lastName: e.target.value })}
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Date of birth (private)
                    <input
                      type="date"
                      value={form.dateOfBirth}
                      onChange={e => setForm({ ...form, dateOfBirth: e.target.value })}
                    />
                    <small>Always kept private. Used by the system for age-based logic only.</small>
                  </label>
                  <label>
                    Joined date
                    <input
                      type="date"
                      value={form.joinedDate}
                      onChange={e => setForm({ ...form, joinedDate: e.target.value })}
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Team *
                    <select
                      value={form.teamId}
                      onChange={e => setForm({ ...form, teamId: e.target.value })}
                      required
                    >
                      <option value="">, Select team, </option>
                      {teams.map(t => <option key={t.id} value={t.id}>{teamLabel(t)}</option>)}
                    </select>
                  </label>
                  <label>
                    Position
                    <select
                      value={form.position}
                      onChange={e => setForm({ ...form, position: e.target.value })}
                    >
                      <option value="">, Select position, </option>
                      {sportConfig.positions.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Jersey number
                    <input
                      type="number"
                      min={0}
                      value={form.jerseyNumber}
                      onChange={e => setForm({ ...form, jerseyNumber: e.target.value })}
                    />
                  </label>
                </div>
              </fieldset>

              {/* Photo */}
              <fieldset>
                <legend>Photo</legend>
                <ImageUploader
                  currentImage={form.photoUrl || undefined}
                  onUpload={url => setForm({ ...form, photoUrl: url })}
                  onUploadingChange={setIsUploadingPhoto}
                  label="Upload photo"
                />
              </fieldset>

              {/* Bio + achievements */}
              <fieldset>
                <legend>About</legend>
                <label>
                  Bio
                  <textarea
                    rows={3}
                    value={form.bio}
                    onChange={e => setForm({ ...form, bio: e.target.value })}
                    placeholder="A short paragraph about the player."
                  />
                </label>
                <label>
                  Achievements (one per line)
                  <textarea
                    rows={4}
                    value={form.achievementsText}
                    onChange={e => setForm({ ...form, achievementsText: e.target.value })}
                    placeholder={"Top scorer 2024/25\nPlayer's player 2023/24"}
                  />
                </label>
              </fieldset>

              {/* Privacy */}
              <fieldset className="privacy-panel">
                <legend>Public visibility</legend>
                <p className="privacy-note">
                  <strong>Default: nothing is public.</strong> Tick only the fields you have explicit
                  permission to display. For under-16 players we strongly recommend leaving the photo and
                  last name off.
                </p>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={form.publicFields.showOnSquadPage}
                    onChange={e => setForm({
                      ...form,
                      publicFields: { ...form.publicFields, showOnSquadPage: e.target.checked },
                    })}
                  />
                  <span><strong>Show this player on the public squad page</strong> (master switch)</span>
                </label>
                <div className="checkbox-grid">
                  {([
                    ['showPhoto', 'Show photo'],
                    ['showLastName', 'Show last name'],
                    ['showJerseyNumber', 'Show jersey number'],
                    ['showPosition', 'Show position'],
                    ['showBio', 'Show bio'],
                    ['showAchievements', 'Show achievements'],
                    ['showStats', 'Show stats'],
                  ] as const).map(([key, label]) => (
                    <label key={key} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={form.publicFields[key]}
                        onChange={e => setForm({
                          ...form,
                          publicFields: { ...form.publicFields, [key]: e.target.checked },
                        })}
                        disabled={!form.publicFields.showOnSquadPage}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Consent */}
              <fieldset className={isPublicallyVisible(form.publicFields) ? 'consent-required' : ''}>
                <legend>
                  Parental consent (kept on file)
                  {isPublicallyVisible(form.publicFields) && (
                    <span className="legend-required">, required to publish</span>
                  )}
                </legend>
                <p className="privacy-note">
                  {isPublicallyVisible(form.publicFields) ? (
                    <>
                      <strong>You've ticked at least one public visibility option above,</strong> so a parental
                      consent record is required before this player can be saved. We retain the record even if
                      the player is later archived, it's a legal record, not just a setting.
                    </>
                  ) : (
                    <>
                      Required as soon as any field above is set to public. The record is retained even if the
                      player is later archived, it's a legal record, not just a setting.
                    </>
                  )}
                </p>
                <div className="form-row">
                  <label>
                    Method {isPublicallyVisible(form.publicFields) && <span className="label-required">*</span>}
                    <select
                      value={form.consent.method || ''}
                      onChange={e => setForm({
                        ...form,
                        consent: { ...form.consent, method: (e.target.value || undefined) as any },
                      })}
                      required={isPublicallyVisible(form.publicFields)}
                    >
                      {CONSENT_METHODS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Parent / guardian name {isPublicallyVisible(form.publicFields) && <span className="label-required">*</span>}
                    <input
                      type="text"
                      value={form.consent.parentName || ''}
                      onChange={e => setForm({
                        ...form,
                        consent: { ...form.consent, parentName: e.target.value },
                      })}
                      required={isPublicallyVisible(form.publicFields)}
                    />
                  </label>
                  <label>
                    Parent / guardian email (optional)
                    <input
                      type="email"
                      value={form.consent.parentEmail || ''}
                      onChange={e => setForm({
                        ...form,
                        consent: { ...form.consent, parentEmail: e.target.value },
                      })}
                    />
                  </label>
                </div>
                <label>
                  Notes
                  <textarea
                    rows={3}
                    value={form.consent.notes || ''}
                    onChange={e => setForm({
                      ...form,
                      consent: { ...form.consent, notes: e.target.value },
                    })}
                    placeholder="e.g. 'Signed consent form filed in club office, scan to follow'"
                  />
                </label>
                {(form.consent.recordedAt || form.consent.lastModifiedAt) && (
                  <div className="consent-audit">
                    {form.consent.recordedAt && (
                      <p className="consent-meta">
                        <strong>Recorded:</strong> {new Date(form.consent.recordedAt).toLocaleString()}
                        {form.consent.recordedBy ? ` by ${form.consent.recordedBy}` : ''}
                      </p>
                    )}
                    {form.consent.lastModifiedAt && (
                      <p className="consent-meta">
                        <strong>Last edited:</strong> {new Date(form.consent.lastModifiedAt).toLocaleString()}
                        {form.consent.lastModifiedBy ? ` by ${form.consent.lastModifiedBy}` : ''}
                      </p>
                    )}
                  </div>
                )}
              </fieldset>

              {/* Guardians, real Member links (features.members). The
                  consent block above stays as the safeguarding artifact;
                  this manages the relational guardian links. Only for an
                  existing player (needs a playerId to link against). */}
              {editingPlayer && membersEnabled && (
                <fieldset>
                  <legend>Guardians</legend>
                  <p className="privacy-note">
                    Link this player to their parent/guardian members. They become
                    real people in your club (Members tab), one parent can cover
                    several children.
                  </p>
                  <GuardianEditor playerId={editingPlayer.playerId} />
                </fieldset>
              )}

              {/* Status */}
              <fieldset>
                <legend>Status</legend>
                <label>
                  <select
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value as PlayerStatus })}
                  >
                    <option value="active">Active (in current squad)</option>
                    <option value="inactive">Inactive (e.g. injured, on loan)</option>
                    <option value="archived">Archived (left the club)</option>
                  </select>
                </label>
              </fieldset>

              <div className="form-actions">
                <button type="button" className="ebtn ebtn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="ebtn ebtn-primary" disabled={isSaving || isUploadingPhoto}>
                  {isSaving ? 'Saving…' : editingPlayer ? 'Save changes' : 'Add player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Stats Modal ──────────────────── */}
      {statsModalPlayer && (
        <div className="players-modal-overlay" onClick={closeStatsModal}>
          <div className="players-modal" onClick={e => e.stopPropagation()}>
            <div className="players-modal-header">
              <h2>Stats, {statsModalPlayer.firstName} {statsModalPlayer.lastName}</h2>
              <button type="button" className="players-modal-close" onClick={closeStatsModal}>×</button>
            </div>

            <div className="stats-form">
              {error && <div className="ealert ealert-error">{error}</div>}

              <p className="stats-intro">
                One block per season. Edit the stats inline. Add another
                season for previous years, they're saved in the same go.
              </p>

              {statsSeasons.map((entry, idx) => (
                <fieldset key={idx} className="season-block">
                  <legend>
                    <input
                      type="text"
                      className="season-block-name"
                      value={entry.season}
                      onChange={e => updateSeasonName(idx, e.target.value)}
                      placeholder={currentSeason(sportConfig.season)}
                      pattern="\d{4}(-\d{2})?"
                      aria-label="Season"
                    />
                    {statsSeasons.length > 1 && (
                      <button
                        type="button"
                        className="season-block-remove"
                        onClick={() => removeSeasonRow(idx)}
                        aria-label={`Remove season ${entry.season}`}
                        title="Remove this season"
                      >
                        ×
                      </button>
                    )}
                  </legend>
                  <div className="stats-grid">
                    {statKeys.map(k => (
                      <label key={k}>
                        {statLabel(k)}{derivedKeys.has(k) ? ' (calculated)' : ''}
                        <input
                          type="number"
                          min={0}
                          value={entry.stats[k] ?? 0}
                          readOnly={derivedKeys.has(k)}
                          onChange={e => updateSeasonStat(idx, k, Number(e.target.value))}
                        />
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}

              {statsSeasons.length === 0 && (
                <p className="stats-empty">No seasons recorded yet.</p>
              )}

              <button
                type="button"
                className="ebtn ebtn-secondary ebtn-sm season-add-btn"
                onClick={addSeasonRow}
              >
                + Add season
              </button>

              <div className="form-actions">
                <button type="button" className="ebtn ebtn-secondary" onClick={closeStatsModal}>Cancel</button>
                <button
                  type="button"
                  className="ebtn ebtn-primary"
                  onClick={handleSaveStats}
                  disabled={isSavingStats}
                >
                  {isSavingStats ? 'Saving…' : 'Save stats'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
